import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Breadcrumb, Card, Descriptions, Button, Space, Tag, Radio, List, message, Modal } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const { Title, Paragraph } = Typography;

const plans = [
  { key: 'basic', name: 'Basic', price: '$49 / month', features: ['Up to 3 campaigns', 'Email support'] },
  { key: 'pro', name: 'Pro', price: '$199 / month', features: ['Unlimited campaigns', 'Priority support', 'Advanced analytics'] },
  { key: 'enterprise', name: 'Enterprise', price: 'Custom', features: ['SLA', 'Dedicated manager', 'SSO'] },
];

const CurrentSubscription = () => {
  const [selected, setSelected] = useState('pro');
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [updateCardOpen, setUpdateCardOpen] = useState(false);
  const { token } = useAuth();

  const stripePromise = useMemo(() => {
    const pk = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    return pk ? loadStripe(pk) : null;
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch('/api/client/billing/subscription', { headers });
      if (!res.ok) throw new Error('Failed to fetch subscription');
      const data = await res.json();
      setSub(data);
      if (data?.planKey) setSelected(data.planKey);
    } catch (e) {
      message.error(e.message || 'Error loading subscription');
    } finally {
      setLoading(false);
    }
  };

  const removeCard = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch('/api/client/billing/remove-default-payment-method', { method: 'POST', headers });
      if (!res.ok) throw new Error('Failed to remove card');
      message.success('Card removed');
      await fetchSubscription();
    } catch (e) {
      message.error(e.message || 'Could not remove card');
    }
  };

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch('/api/client/billing/portal', { headers });
      if (!res.ok) throw new Error('Failed to create portal link');
      const { url } = await res.json();
      if (!url) throw new Error('Portal URL not returned');
      window.location.assign(url);
    } catch (e) {
      message.error(e.message || 'Could not open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const onChangePlan = async () => {
    try {
      setLoading(true);
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch('/api/client/billing/change-plan', {
        method: 'POST',
        headers,
        body: JSON.stringify({ plan: selected }),
      });
      if (!res.ok) throw new Error('Failed to change plan');
      message.success(`Plan changed to ${selected.toUpperCase()}`);
      await fetchSubscription();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Billing' }, { title: 'Current Subscription' }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Current Subscription</Title>
        <Space>
          <Button type="primary" onClick={openPortal} loading={portalLoading}>Manage Payment Method</Button>
          <Button onClick={() => {
            if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
              message.error('Stripe publishable key is not configured');
              return;
              }
            setUpdateCardOpen(true);
          }}>Update Card in App</Button>
          <Button danger onClick={removeCard} disabled={!sub?.card_brand && !sub?.stripe_customer_id}>Remove Card</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary">Your current plan and billing details.</Paragraph>
        <Descriptions bordered column={1} loading={loading}>
          <Descriptions.Item label="Plan">{sub?.plan || sub?.planKey || '-'}</Descriptions.Item>
          <Descriptions.Item label="Status"><Tag color={(sub?.status || sub?.subscription_status) === 'active' ? 'green' : 'red'}>{sub?.status || sub?.subscription_status || '-'}</Tag></Descriptions.Item>
          <Descriptions.Item label="Price">{sub?.price || '-'}</Descriptions.Item>
          <Descriptions.Item label="Seats">{sub?.seats ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Next Billing Date">{sub?.nextBilling || sub?.next_billing_date || '-'}</Descriptions.Item>
          <Descriptions.Item label="Payment Method">{sub?.paymentMethod || '-'}</Descriptions.Item>
          {(sub?.card_brand || sub?.card_last4) && (
            <>
              <Descriptions.Item label="Card Brand">{(sub?.card_brand || '').toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label="Card Last4">{sub?.card_last4}</Descriptions.Item>
              <Descriptions.Item label="Expiry">{sub?.card_exp_month && sub?.card_exp_year ? `${String(sub.card_exp_month).padStart(2, '0')}/${sub.card_exp_year}` : '-'}</Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      <Card style={{ marginTop: 16 }} title="Upgrade/Downgrade Plan" extra={<Button type="primary" onClick={onChangePlan} loading={loading}>Confirm Change</Button>}>
        <Paragraph type="secondary">Select your desired plan.</Paragraph>
        <Radio.Group value={selected} onChange={(e) => setSelected(e.target.value)} style={{ width: '100%' }}>
          <List
            itemLayout="vertical"
            dataSource={plans}
            renderItem={(p) => (
              <List.Item onClick={() => setSelected(p.key)} style={{ cursor: 'pointer' }}>
                <Radio value={p.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      <div style={{ color: '#666' }}>{p.features.join(' • ')}</div>
                    </div>
                    <div style={{ fontWeight: 600 }}>{p.price}</div>
                  </div>
                </Radio>
              </List.Item>
            )}
          />
        </Radio.Group>
      </Card>

      <Modal
        title="Update Payment Method"
        open={updateCardOpen}
        onCancel={() => setUpdateCardOpen(false)}
        footer={null}
        destroyOnClose
     >
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <UpdateCardForm token={token} onSuccess={async () => { setUpdateCardOpen(false); message.success('Payment method updated'); await fetchSubscription(); }} />
          </Elements>
        ) : (
          <Paragraph type="secondary">Stripe publishable key is not configured.</Paragraph>
        )}
      </Modal>
    </div>
  );
};

const UpdateCardForm = ({ token, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const createSetupIntent = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch('/api/client/billing/setup-intent', { method: 'POST', headers });
    if (!res.ok) throw new Error('Failed to create setup intent');
    return res.json();
  };

  const setDefaultPM = async (payment_method_id) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const res = await fetch('/api/client/billing/set-default-payment-method', {
      method: 'POST',
      headers,
      body: JSON.stringify({ payment_method_id }),
    });
    if (!res.ok) throw new Error('Failed to set default payment method');
    return res.json();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    try {
      setSaving(true);
      const { client_secret } = await createSetupIntent();
      const card = elements.getElement(CardElement);
      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card,
        },
      });
      if (result.error) {
        throw new Error(result.error.message || 'Card confirmation failed');
      }
      const pmId = result.setupIntent.payment_method;
      await setDefaultPM(pmId);
      onSuccess?.();
    } catch (err) {
      message.error(err.message || 'Could not update payment method');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div style={{ padding: '12px 8px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 12 }}>
        <CardElement options={{ hidePostalCode: true }} />
      </div>
      <Space>
        <Button onClick={onSubmit} type="primary" loading={saving} disabled={!stripe || !elements}>Save Card</Button>
      </Space>
    </form>
  );
};

export default CurrentSubscription;
